import { useState } from 'react';
import GlassCard from '@/components/base/GlassCard';
import KpiCard from '@/components/base/KpiCard';
import { appointments, matchingCriteria } from '@/mocks/phase2Data';

const apptStatusStyle: Record<string, { bg: string; color: string }> = {
  'Tamamlandı': { bg: 'rgba(16,185,129,0.15)', color: '#10b981' },
  'Devam Ediyor': { bg: 'rgba(255,140,66,0.15)', color: '#ff8c42' },
  'Beklemede': { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
  'İptal Edildi': { bg: 'rgba(239,68,68,0.15)', color: '#ef4444' },
};

const MatchingPage: React.FC = () => {
  const [apptFilter, setApptFilter] = useState('Tümü');
  const filters = ['Tümü', 'Beklemede', 'Devam Ediyor', 'Tamamlandı', 'İptal Edildi'];

  const filtered = appointments.filter((a) => apptFilter === 'Tümü' || a.durum === apptFilter);

  const bekleyen = appointments.filter((a) => a.durum === 'Beklemede').length;
  const devamEden = appointments.filter((a) => a.durum === 'Devam Ediyor').length;
  const tamamlanan = appointments.filter((a) => a.durum === 'Tamamlandı').length;
  const iptalEdilen = appointments.filter((a) => a.durum === 'İptal Edildi').length;

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>Eşleşme & Randevu</h1>
        <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>Akıllı eşleşme algoritması ve randevu yönetimi</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <KpiCard label="Beklemede" value={bekleyen} icon="ri-time-line" />
        <KpiCard label="Devam Ediyor" value={devamEden} change={5.2} icon="ri-loader-line" />
        <KpiCard label="Tamamlandı" value={tamamlanan} change={18.4} icon="ri-check-double-line" />
        <KpiCard label="İptal" value={iptalEdilen} icon="ri-close-circle-line" />
      </div>

      {/* Matching Algorithm Visual */}
      <GlassCard title="Akıllı Eşleşme Algoritması" subtitle="4 temel kriter ile en uygun uzman seçilir" className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {matchingCriteria.map((c, idx) => (
            <div
              key={c.kriter}
              className="rounded-xl p-4 transition-all duration-300 hover:-translate-y-1"
              style={{ background: `${c.renk}0d`, border: `1px solid ${c.renk}33` }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${c.renk}22` }}>
                  <i className={`${c.icon} text-base`} style={{ color: c.renk }}></i>
                </div>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: `${c.renk}22`, color: c.renk }}>%{c.agirlik}</span>
              </div>
              <h4 className="text-sm font-semibold text-white mb-1.5">{c.kriter}</h4>
              <p className="text-xs leading-relaxed" style={{ color: '#6b7280' }}>{c.aciklama}</p>
              <div className="mt-3 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div className="h-1.5 rounded-full" style={{ width: `${c.agirlik * 2.5}%`, background: `linear-gradient(90deg, ${c.renk}, ${c.renk}88)` }} />
              </div>
            </div>
          ))}
        </div>

        {/* Flow visualization */}
        <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
          {[
            { label: 'Müşteri Talebi', icon: 'ri-user-voice-line', color: '#ff4d4d' },
            null,
            { label: 'Algoritma', icon: 'ri-git-merge-line', color: '#ff8c42' },
            null,
            { label: 'En Uygun Uzman', icon: 'ri-user-star-line', color: '#10b981' },
            null,
            { label: 'Randevu', icon: 'ri-calendar-check-line', color: '#ffa94d' },
          ].map((item, i) => {
            if (item === null) {
              return (
                <div key={i} className="flex items-center" style={{ marginTop: '-2px' }}>
                  <div style={{ width: '28px', height: '2px', background: 'linear-gradient(90deg,#ff4d4d,#ff8c42)' }} />
                  <div style={{ width: 0, height: 0, borderTop: '4px solid transparent', borderBottom: '4px solid transparent', borderLeft: '5px solid #ff8c42' }} />
                </div>
              );
            }
            return (
              <div key={i} className="flex flex-col items-center gap-1.5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${item.color}22`, border: `1px solid ${item.color}44` }}>
                  <i className={`${item.icon} text-base`} style={{ color: item.color }}></i>
                </div>
                <span className="text-xs whitespace-nowrap" style={{ color: '#9ca3af', fontSize: '10px' }}>{item.label}</span>
              </div>
            );
          })}
        </div>
      </GlassCard>

      {/* Appointments Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex flex-wrap items-center gap-2 p-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <h3 className="text-sm font-semibold text-white">Randevular</h3>
          <div className="flex gap-1 ml-auto flex-wrap">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setApptFilter(f)}
                className="px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
                style={{
                  background: apptFilter === f ? 'linear-gradient(135deg, rgba(255,77,77,0.25), rgba(255,140,66,0.15))' : 'rgba(255,255,255,0.04)',
                  color: apptFilter === f ? '#ff6b47' : '#6b7280',
                  border: apptFilter === f ? '1px solid rgba(255,77,77,0.3)' : '1px solid transparent',
                }}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Müşteri', 'Uzman', 'Hizmet', 'Tarih & Saat', 'Konum', 'Ücret', 'Durum'].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: '#6b7280' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, idx) => (
                <tr key={a.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: idx % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                  <td className="px-4 py-3 text-white font-medium">{a.musteri}</td>
                  <td className="px-4 py-3 text-gray-300">{a.uzman}</td>
                  <td className="px-4 py-3 text-gray-300">{a.hizmet}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{a.tarih}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{a.konum}</td>
                  <td className="px-4 py-3 font-medium text-white text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{a.ucret} ₺</td>
                  <td className="px-4 py-3">
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap" style={{ background: apptStatusStyle[a.durum]?.bg, color: apptStatusStyle[a.durum]?.color }}>
                      {a.durum}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MatchingPage;
